import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Footer } from '../containers';
import HexagonBackground from '../components/products/HexagonBackground';
import { FiSearch, FiCpu, FiCheckCircle } from 'react-icons/fi';
import './pages.css';

const supportData = [
    {
        category: 'Bosch Kefico ECU Models',
        description: 'Supported high-performance Bosch Kefico ECU units with detailed MCU information.',
        items: [
            { name: 'CPEGD2.20.1', detail: 'TC1782' },
            { name: 'CPEGD2.20.2', detail: 'TC1782' },
            { name: 'CPEGD2.20.3', detail: 'TC1782' },
            { name: 'CPEGD2.20.4', detail: 'TC1782' },
            { name: 'CPEGD3.20.1/CPEGP3.20.1', detail: 'Aurix TC277TP' },
            { name: 'CPEGP2.10.1', detail: 'TC1728' },
            { name: 'CPEGP3.20.2/CPGPSH3.24.1', detail: 'Aurix TC275TP' },
            { name: 'CPGDSH2.26.1', detail: 'TC1782' },
            { name: 'CPGDSH2.2x.x/CPEGD2.20.x', detail: 'TC1782' },
            { name: 'CPGPSH2.14.1', detail: 'TC1728' },
            { name: 'CPGPSH3.26.1/CPGDSH3.26.2', detail: 'Aurix TC275TP' }
        ]
    },
    {
        category: 'Common Denso ECU Series',
        description: 'Widespread Denso series found in Nissan and other diesel applications.',
        items: [
            { name: 'MB275700-xxxx', detail: 'Commonly used in Nissan Diesel' },
            { name: 'MB275800-xxxx', detail: 'Almera, Navara, etc.' },
            { name: 'Denso SH70xx', detail: 'SH7058/SH7059' },
            { name: 'Denso D3/Dxx Series', detail: 'D3, E30QU4D' },
            { name: 'Denso VEA', detail: 'Renesas or Freescale MCUs' }
        ]
    },
    {
        category: 'Specific Nissan Denso Hardware',
        description: 'Direct hardware/part number support for Nissan Denso units.',
        items: [
            { name: '23710 EC07D', detail: 'Navara 2.5 DCI - D3' },
            { name: '23710 BN821', detail: 'Almera II 2.2 DCI/VDI - HZ' },
            { name: '23710 LC10D', detail: 'Cabstar II 2.5 DCI/VDI - C6' },
            { name: '23710 BU77A', detail: '2.2 DCI' },
            { name: '275800-4454', detail: 'X-Trail - FQ' },
            { name: '275800-2956', detail: 'X-Trail - 2' },
            { name: '275700-2191', detail: 'Navara 2.5' },
            { name: '275700-3591', detail: 'Navara 2.5 DCI' }
        ]
    },
    {
        category: 'Nissan Hitachi GEN1-GEN2 (1MB-512KB)',
        description: 'Full IMMO-OFF support for Hitachi generation 1 and 2 units.',
        items: [
            { name: 'SH705415N', detail: 'Hitachi MCU' },
            { name: 'SH705507N', detail: 'Hitachi MCU' },
            { name: 'SH705513N', detail: 'Hitachi MCU' },
            { name: 'SH705519N', detail: 'Hitachi MCU' },
            { name: 'SH705520N', detail: 'Hitachi MCU' },
            { name: 'SH705524N', detail: 'Hitachi MCU' },
            { name: 'SH705529N', detail: 'Hitachi MCU' },
            { name: 'SH705830N', detail: 'Hitachi MCU' },
            { name: 'SH705821N', detail: 'Hitachi MCU' },
            { name: 'SH705822N', detail: 'Hitachi MCU' },
            { name: 'SH705823N', detail: 'Hitachi MCU' },
            { name: 'SH705927N', detail: 'Hitachi MCU' },
            { name: 'SH705828N', detail: 'Hitachi MCU' },
            { name: 'SH705826N', detail: 'Hitachi MCU' }
        ]
    },
    {
        category: 'Renault/Nissan UDS Hitachi 4MB',
        description: 'Advanced support for UDS Hitachi 4MB units including Cat-Off and Grill Shutter solutions.',
        items: [
            { name: 'CAT-OFF / DTC Delete', detail: 'P0420, P2097, P0139, P2270, P2271, P2A01, P0038, P0037, P0141, P0136, P0138, P0137' },
            { name: 'Active Grill Shutter', detail: 'Disable P059F, P159F, U0284' }
        ]
    },
    {
        category: 'Common Vehicle Applications',
        description: 'Vehicles frequently equipped with these supported ECU units.',
        items: [
            { name: 'Nissan Navara', detail: '2.5 dCi' },
            { name: 'Nissan Almera II', detail: '2.2 dCi' },
            { name: 'Nissan Cabstar', detail: '2.5 dCi' },
            { name: 'Nissan X-Trail', detail: 'T30/T31' },
            { name: 'Nissan Primera', detail: 'P12' }
        ]
    }
];

const SupportList = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState(supportData);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const filtered = supportData.map(cat => ({
            ...cat,
            items: cat.items.filter(item =>
                item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.detail.toLowerCase().includes(searchTerm.toLowerCase())
            )
        })).filter(cat => cat.items.length > 0);

        setFilteredData(filtered);
    }, [searchTerm]);

    return (
        <div className="gpt3__support-list">
            <HexagonBackground />

            <div className="gpt3__support-list_container section__padding">
                <motion.div
                    className="gpt3__support-list_header"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <h1 className="gradient__text">ECU Hardware Support List</h1>
                    <p>Comprehensive list of supported hardware, ECUs, and vehicle models for professional tuning.</p>

                    <div className="gpt3__support-list_search">
                        <FiSearch />
                        <input
                            type="text"
                            placeholder="Search by ECU model, hardware number, or vehicle..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </motion.div>

                <div className="gpt3__support-list_content">
                    {filteredData.map((category, index) => (
                        <motion.div
                            key={index}
                            className="gpt3__support-list_category"
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                        >
                            <div className="category-header">
                                <FiCpu />
                                <div>
                                    <h2>{category.category}</h2>
                                    <p>{category.description}</p>
                                </div>
                            </div>

                            <div className="category-items">
                                {category.items.map((item, i) => (
                                    <div key={i} className="support-item">
                                        <div className="item-name">
                                            <FiCheckCircle />
                                            <span>{item.name}</span>
                                        </div>
                                        <div className="item-detail">{item.detail}</div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    ))}

                    {filteredData.length === 0 && (
                        <div className="no-results">
                            <h3>No hardware found matching "{searchTerm}"</h3>
                            <p>Try searching with a different keyword or contact our support team.</p>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default SupportList;
